import React, { useEffect } from "react";
import styles from "../styles/Merchant.module.css";
import qrStyles from "../styles/QR.module.css";
import equbPersons from "../assets/ArifEqubPersons.png";
import equbAppDownload from "../assets/Arif EqubAppDownload.png";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Link, useLocation } from "react-router-dom";
import {
  FaApple,
  FaGooglePlay,
  FaStore,
  FaHome,
  FaGlobeAmericas,
  FaUserGraduate,
  FaClipboardList,
  FaSyncAlt,
  FaCoins,
  FaUserPlus,
  FaShieldAlt,
  FaBell,
  FaLock,
  FaChartLine,
  FaExchangeAlt,
  FaCheckCircle,
} from "react-icons/fa";

const GOOGLE_PLAY_URL =
  "#";

const introHighlights = [
  { label: "Live ledgers", icon: FaClipboardList },
  { label: "Automated draws", icon: FaSyncAlt },
  { label: "Multi-currency", icon: FaCoins, comingSoon: true },
];

const features = [
  {
    title: "Create or join any group",
    desc: "Set your contribution amount, cycle frequency, and number of members in minutes.",
    icon: FaUserPlus,
  },
  {
    title: "Live group ledger",
    desc: "Every member sees who has paid, who is pending, and when the next payout lands.",
    icon: FaClipboardList,
  },
  {
    title: "Tamper-proof draw",
    desc: "Automated lottery rotation — no manual errors, no disputes, no lost notebooks.",
    icon: FaShieldAlt,
  },
  {
    title: "Automatic reminders",
    desc: "SMS and push alerts before each due date keep your group running at 100%.",
    icon: FaBell,
  },
  {
    title: "Funds held in escrow",
    desc: "All money is secured by Meftehe Microfinance, a fully licensed institution.",
    icon: FaLock,
  },
  {
    title: "Build your credit history",
    desc: "Regular contributions create a digital record that unlocks Meftehe microfinance loans.",
    icon: FaChartLine,
  },
];

const audiences = [
  {
    title: "Merchants",
    desc: "Digitize your Equb, cut cash risk, unlock microfinance loans.",
    icon: FaStore,
  },
  {
    title: "Households",
    desc: "Transparent, automated savings with your community group.",
    icon: FaHome,
  },
  {
    title: "Diaspora",
    desc: "Contribute in USD, EUR, GBP or AED to your family's circle back home.",
    icon: FaGlobeAmericas,
    comingSoon: true,
  },
  {
    title: "Young savers",
    desc: "Flexible daily or weekly saving to start your financial journey.",
    icon: FaUserGraduate,
  },
];

const currencies = [
  { region: "ET", code: "ETB", country: "Ethiopia", active: true },
  { region: "US", code: "USD", country: "United States", comingSoon: true },
  { region: "GB", code: "GBP", country: "United Kingdom", comingSoon: true },
  { region: "EU", code: "EUR", country: "Europe", comingSoon: true },
  { region: "AE", code: "AED", country: "UAE", comingSoon: true },
  { region: "SA", code: "SAR", country: "Saudi Arabia", comingSoon: true },
  { region: "CA", code: "CAD", country: "Canada", comingSoon: true },
];

const getStartedSteps = [
  {
    step: 1,
    title: "Download the app",
    desc: "Available on iOS and Android, free to sign up.",
  },
  {
    step: 2,
    title: "Create or join a group",
    desc: "Set your rules or accept an invite from your organizer.",
  },
  {
    step: 3,
    title: "Contribute regularly",
    desc: "Pay in ETB from your bank or mobile wallet.",
  },
  {
    step: 4,
    title: "Receive your payout",
    desc: "When your turn comes, funds go straight to your account.",
  },
  {
    step: 5,
    title: "Unlock credit",
    desc: "Your savings history opens the door to Meftehe loans.",
  },
];

const whyPoints = [
  {
    text: "Digital Equb in ETB — save together across Ethiopia",
    icon: FaCheckCircle,
  },
  {
    text: "NBE-compliant and fully transparent",
    icon: FaShieldAlt,
  },
  {
    text: "Secured by Meftehe Microfinance",
    icon: FaLock,
  },
  {
    text: "Equb with dollar — save together, from anywhere",
    icon: FaGlobeAmericas,
    comingSoon: true,
  },
  {
    text: "Multi-currency contributions via Arif Online Payment Gateway international payments",
    icon: FaExchangeAlt,
    comingSoon: true,
  },
];

const ArifEqub = () => {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  return (
    <div>
      <Header />
      <div className={styles.equbPage}>
        {/* Hero — ArifQR side-by-side layout */}
        <div
          className={`${qrStyles.empoweringSection} ${qrStyles.equbHeroSection}`}
        >
          <div className={qrStyles.hero}>
            <div className={qrStyles.heroContent}>
              <p className={qrStyles.msmesLabel}>Arif Equb</p>
              <h1 className={qrStyles.heroTitle}>What is Arif Equb</h1>
              <p className={qrStyles.equbHeroTagline}>
                <span>Digital Equb in ETB — save together, as a community</span>
              </p>
              <p className={qrStyles.equbHeroAmharic}>
                ዲጂታል እቁብ በብር — አብረን እንጣል
              </p>
              <p className={qrStyles.equbHeroDesc}>
                Your Equb, fully digital. Contribute in ETB, track every payout
                in real time, and keep your savings circle running smoothly
                across Ethiopia. International payments are coming soon.
              </p>
              <p className={styles.equbHeroComingSoonNote}>
                <span className={styles.equbComingSoonBadge}>Coming Soon</span>
                ዲጂታል እቁብ በዶላር — save together, from anywhere
              </p>

              <Link to="/ContactUs">
                <button className={qrStyles.contactButton}>Contact Us →</button>
              </Link>
            </div>
            <div className={qrStyles.heroImage}>
              <div className={qrStyles.imageWithButtons}>
                <img
                  className={qrStyles.equbHeroImage}
                  src={equbPersons}
                  alt="Arif Pay Digital Equb"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Key features intro */}
        <section className={styles.equbFeaturesIntro}>
          <div className={styles.equbFeaturesIntroCard}>
            <span className={styles.equbFeaturesIntroBadge}>KEY FEATURES</span>
            <h3 className={styles.equbIntroTitle}>Built for Every Saver</h3>
            <p className={styles.equbIntroPara}>
              With a sleek interface and intuitive tools, Arif Equb transforms
              group savings into a secure, digital experience — from live ledgers
              to automated draws in ETB, it&apos;s all in your hands. Multi-currency
              support is coming soon.
            </p>
            <div className={styles.equbFeaturesIntroPills}>
              {introHighlights.map(({ label, icon, comingSoon }) => {
                const HighlightIcon = icon;
                return (
                  <span
                    key={label}
                    className={`${styles.equbFeaturesIntroPill} ${
                      comingSoon ? styles.equbFeaturesIntroPillSoon : ""
                    }`}
                  >
                    <HighlightIcon aria-hidden />
                    {label}
                    {comingSoon && (
                      <span className={styles.equbComingSoonBadge}>Coming Soon</span>
                    )}
                  </span>
                );
              })}
            </div>
          </div>
        </section>

        {/* Feature cards */}
        <section className={styles.equbFeaturesSection}>
          <div className={styles.equbFeaturesGrid}>
            {features.map(({ title, desc, icon }) => {
              const FeatureIcon = icon;
              return (
                <article key={title} className={styles.equbFeatureCard}>
                  <div className={styles.equbFeatureIconWrap}>
                    <FeatureIcon aria-hidden />
                  </div>
                  <div className={styles.equbFeatureCardBody}>
                    <h4 className={styles.equbFeatureCardTitle}>{title}</h4>
                    <p className={styles.equbFeatureCardDesc}>{desc}</p>
                  </div>
                </article>
              );
            })}
          </div>
        </section>

        {/* Who it's for */}
        <section className={styles.equbAudienceSection}>
          <div className={styles.topHeader}>
            <p className={styles.equbSectionLabel}>WHO IT&apos;S FOR</p>
            <h2 className={styles.equbSectionTitle}>
              Built for Every Community
            </h2>
          </div>
          <div className={styles.equbAudienceGrid}>
            {audiences.map(({ title, desc, icon, comingSoon }) => {
              const AudienceIcon = icon;
              return (
              <article
                key={title}
                className={`${styles.equbAudienceCard} ${
                  comingSoon ? styles.equbAudienceCardSoon : ""
                }`}
              >
                {comingSoon && (
                  <span className={styles.equbAudienceSoonBadge}>Coming Soon</span>
                )}
                <div className={styles.equbAudienceIconWrap}>
                  <AudienceIcon aria-hidden />
                </div>
                <div className={styles.equbAudienceCardBody}>
                  <h4 className={styles.equbAudienceTitle}>{title}</h4>
                  <p className={styles.equbAudienceDesc}>{desc}</p>
                </div>
              </article>
              );
            })}
          </div>
        </section>

        {/* Multi-currency */}
        <section className={styles.equbMultiCurrencySection}>
          <div className={styles.topHeader}>
            <p className={styles.equbSectionLabel}>MULTI-CURRENCY EQUB</p>
            <span className={styles.equbSectionComingSoonBadge}>Coming Soon</span>
            <h2 className={styles.equbSectionTitle}>How It Will Work</h2>
          </div>
          <p className={styles.equbMultiDesc}>
            Arif Equb currently supports contributions in ETB. Soon, members living
            abroad will be able to pay in their local currency — Arif Equb will
            convert it in real time through Arif Online Payment Gateway
            international payments and deposit the exact ETB equivalent into the
            shared pool.
          </p>
          <div className={styles.equbCurrencyGrid}>
            {currencies.map(({ region, code, country, comingSoon, active }) => (
              <div
                key={code}
                className={`${styles.equbCurrencyCard} ${
                  comingSoon ? styles.equbCurrencyCardSoon : styles.equbCurrencyCardActive
                }`}
              >
                {active && (
                  <span className={styles.equbCurrencyActiveBadge}>Available Now</span>
                )}
                {comingSoon && (
                  <span className={styles.equbCurrencySoonBadge}>Coming Soon</span>
                )}
                <p className={styles.equbCurrencyRegion}>{region}</p>
                <p className={styles.equbCurrencyCode}>{code}</p>
                <p className={styles.equbCurrencyCountry}>{country}</p>
              </div>
            ))}
          </div>
          <p className={`${styles.equbFlowLine} ${styles.equbFlowLineSoon}`}>
            <span className={styles.equbComingSoonBadge}>Coming Soon</span>
            You pay in your currency → Arif Online Payment Gateway international
            payments convert instantly → ETB lands in the Equb pool → Winner
            receives in ETB
          </p>
          <p className={styles.equbFlowSub}>
            Live exchange rates · Zero middleman markup · Instant digital receipt
            · 100% NBE and AML compliant
          </p>
        </section>

        {/* How to get started */}
        <section>
          <div className={styles.topHeader}>
            <p className={styles.equbSectionLabel}>HOW TO GET STARTED</p>
            <h2 className={styles.equbSectionTitle}>Five Simple Steps</h2>
          </div>
          <div className={styles.equbStepsGrid}>
            {getStartedSteps.map(({ step, title, desc }) => (
              <div key={step} style={{ textAlign: "left" }}>
                <div className={styles.equbStepBadge}>{step}</div>
                <h4 className={styles.equbStepTitle}>{title}</h4>
                <p className={styles.equbStepDesc}>{desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Why Arif Equb */}
        <section className={styles.equbWhySection}>
          <div className={styles.topHeader}>
            <p className={styles.equbSectionLabel}>ARIF EQUB APP</p>
            <h2 className={styles.equbSectionTitle}>Why trust Arif Equb?</h2>
          </div>
          <div className={styles.equbWhyLayout}>
            <div className={styles.equbWhyImageWrap}>
              <img
                src={equbPersons}
                alt="Arif Equb community"
                className={styles.equbWhyImage}
              />
            </div>
            <ul className={styles.equbWhyList}>
              {whyPoints.map(({ text, icon, comingSoon }) => {
                const WhyIcon = icon;
                return (
                  <li
                    key={text}
                    className={`${styles.equbWhyItem} ${
                      comingSoon ? styles.equbWhyItemSoon : ""
                    }`}
                  >
                    <div className={styles.equbWhyItemIcon}>
                      <WhyIcon aria-hidden />
                    </div>
                    <span className={styles.equbWhyItemText}>
                      {text}
                      {comingSoon && (
                        <span className={styles.equbComingSoonBadge}>Coming Soon</span>
                      )}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        </section>

        {/* Download CTA */}
        <div className={styles.equbDownloadBanner}>
          <div className={styles.equbDownloadCard}>
            <div className={styles.equbDownloadInner}>
              <div className={styles.equbDownloadImgWrap}>
                <img
                  src={equbAppDownload}
                  alt="Download Arif Equb"
                  className={styles.equbDownloadImg}
                />
              </div>
              <div className={styles.equbGetApp}>
                <h2 className={styles.equbDownloadTitle}>Get the App Today</h2>
                <p className={styles.equbDownloadDesc}>
                  <span className={styles.equbDownloadSpark}>✦</span>
                  Take your savings circle to the next level. Download Arif Equb
                  from the App Store or Google Play and enjoy hassle-free digital
                  group savings.
                </p>
                <div className={styles.storeButtons}>
                  <div
                    className={`${styles.storeButton} ${styles.storeButtonApple}`}
                    aria-label="App Store — Coming Soon"
                  >
                    <FaApple className={styles.storeButtonIcon} aria-hidden />
                    <span className={styles.storeButtonText}>
                      <span className={styles.storeButtonSmall}>
                        Download on the
                      </span>
                      <span className={styles.storeButtonBig}>App Store</span>
                      <span className={styles.storeButtonBadge}>Coming Soon</span>
                    </span>
                  </div>
                  <a
                    href={GOOGLE_PLAY_URL}
                    // target="_blank"
                    rel="noopener noreferrer"
                    className={`${styles.storeButton} ${styles.storeButtonGoogle}`}
                    aria-label="Get it on Google Play"
                  >
                    <FaGooglePlay className={styles.storeButtonIcon} aria-hidden />
                    <span className={styles.storeButtonText}>
                      <span className={styles.storeButtonSmall}>Get it on</span>
                      <span className={styles.storeButtonBig}>Google Play</span>
                    </span>
                  </a>
                </div>
              </div>
            </div>
            <p className={styles.equbPoweredBy}>
              Powered by Arifpay · Secured by Meftehe Microfinance · International
              payments via Arif Online Payment Gateway — Coming Soon
            </p>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ArifEqub;
